import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ONBOARDING } from '@/constants/testIds';
import { ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';

const STEPS = ['Company', 'Goals', 'Channels', 'Stack'];

function blankForm(initial = {}) {
  return {
    company_name: '',
    website: '',
    industry: '',
    target_geography: '',
    target_customers: '',
    services: '',
    products: '',
    locations: '',
    competitors: '',
    primary_goals: '',
    revenue_goals: '',
    ai_goals: '',
    marketing_goals: '',
    linkedin: '',
    x_twitter: '',
    facebook: '',
    instagram: '',
    youtube: '',
    google_business_profile: '',
    crm_platform: '',
    cms: '',
    email_marketing_platform: '',
    technology_stack: '',
    reporting_frequency: 'weekly',
    ...stringify(initial),
  };
}

function stringify(client) {
  if (!client) return {};
  const csvFields = ['services', 'products', 'locations', 'competitors', 'technology_stack'];
  const out = { ...client };
  csvFields.forEach(f => {
    if (Array.isArray(out[f])) out[f] = out[f].join(', ');
  });
  return out;
}

/**
 * Shared intake form used both for /onboarding (new client) and /intake/:clientId (existing client completes intake).
 * Props:
 *   - initialClient: optional client doc to prefill
 *   - submitLabel: button text on the final step
 *   - intro: header content (title + subtitle JSX)
 *   - onSubmit: async (payloadWithCsvParsed) => void
 *   - submitting: boolean
 */
export default function IntakeWizard({ initialClient, submitLabel = 'Activate AuthorityOS', intro, onSubmit, submitting }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blankForm(initialClient));
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const csv = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);

  const submit = () => {
    const payload = {
      ...form,
      services: csv(form.services),
      products: csv(form.products),
      locations: csv(form.locations),
      competitors: csv(form.competitors),
      technology_stack: csv(form.technology_stack),
    };
    onSubmit(payload);
  };

  return (
    <div className="max-w-3xl mx-auto">
      {intro}

      <div className="mb-6">
        <div className="flex justify-between text-xs text-muted-foreground mb-2">
          <span>Step {step + 1} of {STEPS.length}</span>
          <span>{STEPS[step]}</span>
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} />
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        {step === 0 && (
          <>
            <Field label="Company name *">
              <Input data-testid={ONBOARDING.companyName} value={form.company_name} onChange={e => update('company_name', e.target.value)} placeholder="Acme, Inc." />
            </Field>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Website *">
                <Input data-testid={ONBOARDING.website} value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://acme.com" />
              </Field>
              <Field label="Industry *">
                <Input data-testid={ONBOARDING.industry} value={form.industry} onChange={e => update('industry', e.target.value)} placeholder="SaaS / FinTech / Health" />
              </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Target geography"><Input value={form.target_geography} onChange={e => update('target_geography', e.target.value)} placeholder="North America" /></Field>
              <Field label="Target customers"><Input value={form.target_customers} onChange={e => update('target_customers', e.target.value)} placeholder="Mid-market CFOs" /></Field>
            </div>
            <Field label="Competitors (comma separated URLs)">
              <Input data-testid={ONBOARDING.competitors} value={form.competitors} onChange={e => update('competitors', e.target.value)} placeholder="competitor1.com, competitor2.com" />
            </Field>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Services (comma separated)"><Input value={form.services} onChange={e => update('services', e.target.value)} placeholder="Consulting, Implementation" /></Field>
              <Field label="Products (comma separated)"><Input value={form.products} onChange={e => update('products', e.target.value)} placeholder="Product A, Product B" /></Field>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <Field label="Primary goals"><Textarea value={form.primary_goals} onChange={e => update('primary_goals', e.target.value)} placeholder="Become the recognized authority in our category." rows={3} /></Field>
            <Field label="Revenue goals"><Textarea value={form.revenue_goals} onChange={e => update('revenue_goals', e.target.value)} placeholder="$10M ARR in 18 months" rows={2} /></Field>
            <Field label="AI goals"><Textarea value={form.ai_goals} onChange={e => update('ai_goals', e.target.value)} placeholder="Be cited and recommended by leading AI assistants" rows={2} /></Field>
            <Field label="Marketing goals"><Textarea value={form.marketing_goals} onChange={e => update('marketing_goals', e.target.value)} placeholder="3x organic leads" rows={2} /></Field>
            <Field label="Reporting frequency">
              <Select value={form.reporting_frequency} onValueChange={v => update('reporting_frequency', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </>
        )}

        {step === 2 && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="LinkedIn"><Input value={form.linkedin} onChange={e => update('linkedin', e.target.value)} /></Field>
              <Field label="X / Twitter"><Input value={form.x_twitter} onChange={e => update('x_twitter', e.target.value)} /></Field>
              <Field label="Facebook"><Input value={form.facebook} onChange={e => update('facebook', e.target.value)} /></Field>
              <Field label="Instagram"><Input value={form.instagram} onChange={e => update('instagram', e.target.value)} /></Field>
              <Field label="YouTube"><Input value={form.youtube} onChange={e => update('youtube', e.target.value)} /></Field>
              <Field label="Google Business Profile"><Input value={form.google_business_profile} onChange={e => update('google_business_profile', e.target.value)} /></Field>
            </div>
            <Field label="Locations (comma separated)"><Input value={form.locations} onChange={e => update('locations', e.target.value)} placeholder="New York, London, Singapore" /></Field>
          </>
        )}

        {step === 3 && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="CRM platform"><Input value={form.crm_platform} onChange={e => update('crm_platform', e.target.value)} placeholder="HubSpot" /></Field>
            <Field label="CMS"><Input value={form.cms} onChange={e => update('cms', e.target.value)} placeholder="WordPress / Webflow" /></Field>
            <Field label="Email marketing"><Input value={form.email_marketing_platform} onChange={e => update('email_marketing_platform', e.target.value)} placeholder="Mailchimp / HubSpot" /></Field>
            <Field label="Technology stack (comma separated)"><Input value={form.technology_stack} onChange={e => update('technology_stack', e.target.value)} placeholder="React, Node, Postgres" /></Field>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <Button data-testid={ONBOARDING.back} variant="outline" disabled={step === 0} onClick={() => setStep(s => Math.max(0, s - 1))}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button data-testid={ONBOARDING.next} onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))} disabled={step === 0 && !form.company_name.trim()}>
            Continue <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button data-testid={ONBOARDING.submit} onClick={submit} disabled={submitting || !form.company_name.trim()}>
            <Sparkles className="h-4 w-4 mr-1" /> {submitting ? 'Submitting…' : submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
